import './Header.scss';

import { Divider, Icon, Typography } from '@modulor/react';
import clx from 'classnames';
import { HeaderContext } from 'providers/HeaderProvider';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { hideNavSelector } from 'store/slices/global';
import { HEADER_ICON, inIframe } from 'utils';

import { CardsActionButton } from './CardsActionButton';
import { useHeaderNavigation } from './hooks/useHeaderNavigation';
import { useHeaderTitle } from './hooks/useHeaderTitle';
import { useShowHeader } from './hooks/useShowHeader';

const Header = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('Header must be used within HeaderProvider');
  }
  const { config } = context;
  const { navIcon, handleNavClick, handleCloseClick } = useHeaderNavigation();
  const { title, subtitle } = useHeaderTitle();
  const curLocation = useLocation();
  const hideNav = useAppSelector(hideNavSelector);
  const showHeader = useShowHeader({
    curLocation,
    hideNav,
  });
  if (!showHeader) return null;

  return (
    <div
      className={clx('header-wrapper', {
        'header-wrapper__in-iframe': inIframe(),
      })}
    >
      <div className="header-container">
        {/* Left nav button (back or close on status pages) */}
        {!config.hideNavLeft && !config.hideNav && !config.showCloseRight && (
          <div className="header-button" onClick={handleNavClick} data-icon={HEADER_ICON[navIcon]}>
            <Icon name={HEADER_ICON[navIcon]} />
          </div>
        )}

        {/* Back button on left when showCloseRight — always in DOM to prevent flash on tab switch */}
        {!config.hideNavLeft && !config.hideNav && config.showCloseRight && (
          <div
            className={clx('header-button', {
              'header-button--hidden': config.navIcon !== 'back',
            })}
            onClick={handleNavClick}
            data-icon={HEADER_ICON.back}
          >
            <Icon name={HEADER_ICON.back} />
          </div>
        )}

        <div className="centered-text-block">
          <Typography className="header-title" variant="title-2-semibold" data-translation-key={config.title}>
            {title}
          </Typography>
          {subtitle && <Typography variant="caption-1-regular">{subtitle}</Typography>}
        </div>

        {/* Right close button (Init/Select/Submit/Staus pages) */}
        {config.showCloseRight && (
          <span
            className="action-button action-button--close"
            data-testid="header-close-right"
            role="button"
            tabIndex={0}
            onClick={handleCloseClick}
          >
            <Icon name="close" />
          </span>
        )}

        {/* Cards delete button (only on Cards page) */}
        <span className="action-button">
          <CardsActionButton />
        </span>
      </div>
      <Divider
        className={clx('header-divider', {
          'header-divider__in-iframe': inIframe(),
        })}
      />
    </div>
  );
};

export default Header;
